import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { ICirStoreCollection, ICirWeekCollection } from 'common/models/commonModel';
import { AppDispatch } from 'app/store';
import { analyzeChannelsTransport } from 'entities/AnalyzeChannels/AnalyzeChannels.transport';
import {
  IAnalyzeChannelsChannelShareModel,
  IAnalyzeChannelsChannelShareParams,
  IAnalyzeChannelsChannelShareShiftsModel,
  IAnalyzeChannelsChannelShareShiftsParams,
  IAnalyzeChannelsChannelShareWeeklyModel,
  IAnalyzeChannelsChannelShareWeeklyParams,
} from 'entities/AnalyzeChannels/AnalyzeChannels.models';

export interface IState {
  // stores
  analyzeChannelsStoreCollectionLoading: boolean;
  analyzeChannelsStoreCollection: ICirStoreCollection | null;
  analyzeChannelsStoreCollectionError: IError | null;
  // weeks
  analyzeChannelsWeekCollectionLoading: boolean;
  analyzeChannelsWeekCollection: ICirWeekCollection | null;
  analyzeChannelsWeekCollectionError: IError | null;
  // channel share
  analyzeChannelsChannelShareModelLoading: boolean;
  analyzeChannelsChannelShareModel: IAnalyzeChannelsChannelShareModel | null;
  analyzeChannelsChannelShareModelError: IError | null;
  // channel share weekly
  analyzeChannelsChannelShareWeeklyModelLoading: boolean;
  analyzeChannelsChannelShareWeeklyModel: IAnalyzeChannelsChannelShareWeeklyModel | null;
  analyzeChannelsChannelShareWeeklyModelError: IError | null;
  // channel share shifts
  analyzeChannelsChannelShareShiftsModelLoading: boolean;
  analyzeChannelsChannelShareShiftsModel: IAnalyzeChannelsChannelShareShiftsModel | null;
  analyzeChannelsChannelShareShiftsModelError: IError | null;
}

const initialState: IState = {
  // stores
  analyzeChannelsStoreCollectionLoading: false,
  analyzeChannelsStoreCollection: null,
  analyzeChannelsStoreCollectionError: null,
  // weeks
  analyzeChannelsWeekCollectionLoading: false,
  analyzeChannelsWeekCollection: null,
  analyzeChannelsWeekCollectionError: null,
  // channel share
  analyzeChannelsChannelShareModelLoading: false,
  analyzeChannelsChannelShareModel: null,
  analyzeChannelsChannelShareModelError: null,
  // channel share weekly
  analyzeChannelsChannelShareWeeklyModelLoading: false,
  analyzeChannelsChannelShareWeeklyModel: null,
  analyzeChannelsChannelShareWeeklyModelError: null,
  // channel share shifts
  analyzeChannelsChannelShareShiftsModelLoading: false,
  analyzeChannelsChannelShareShiftsModel: null,
  analyzeChannelsChannelShareShiftsModelError: null,
};

export const analyzeChannelsSlice = createSlice({
  name: 'analyzeChannels',
  initialState,
  reducers: {
    // stores
    setAnalyzeChannelsStoreCollectionLoading(state, action: PayloadAction<boolean>) {
      state.analyzeChannelsStoreCollectionLoading = action.payload;
    },
    setAnalyzeChannelsStoreCollection(state, action: PayloadAction<ICirStoreCollection | null>) {
      state.analyzeChannelsStoreCollection = action.payload;
    },
    setAnalyzeChannelsStoreCollectionError(state, action: PayloadAction<IError | null>) {
      state.analyzeChannelsStoreCollectionError = action.payload;
    },
    // weeks
    setAnalyzeChannelsWeekCollectionLoading(state, action: PayloadAction<boolean>) {
      state.analyzeChannelsWeekCollectionLoading = action.payload;
    },
    setAnalyzeChannelsWeekCollection(state, action: PayloadAction<ICirWeekCollection | null>) {
      state.analyzeChannelsWeekCollection = action.payload;
    },
    setAnalyzeChannelsWeekCollectionError(state, action: PayloadAction<IError | null>) {
      state.analyzeChannelsWeekCollectionError = action.payload;
    },
    // channel share
    setAnalyzeChannelsChannelShareModelLoading(state, action: PayloadAction<boolean>) {
      state.analyzeChannelsChannelShareModelLoading = action.payload;
    },
    setAnalyzeChannelsChannelShareModel(state, action: PayloadAction<IAnalyzeChannelsChannelShareModel | null>) {
      state.analyzeChannelsChannelShareModel = action.payload;
    },
    setAnalyzeChannelsChannelShareModelError(state, action: PayloadAction<IError | null>) {
      state.analyzeChannelsChannelShareModelError = action.payload;
    },
    // channel share weekly
    setAnalyzeChannelsChannelShareWeeklyModelLoading(state, action: PayloadAction<boolean>) {
      state.analyzeChannelsChannelShareWeeklyModelLoading = action.payload;
    },
    setAnalyzeChannelsChannelShareWeeklyModel(
      state,
      action: PayloadAction<IAnalyzeChannelsChannelShareWeeklyModel | null>
    ) {
      state.analyzeChannelsChannelShareWeeklyModel = action.payload;
    },
    setAnalyzeChannelsChannelShareWeeklyModelError(state, action: PayloadAction<IError | null>) {
      state.analyzeChannelsChannelShareWeeklyModelError = action.payload;
    },
    // channel share shifts
    setAnalyzeChannelsChannelShareShiftsModelLoading(state, action: PayloadAction<boolean>) {
      state.analyzeChannelsChannelShareShiftsModelLoading = action.payload;
    },
    setAnalyzeChannelsChannelShareShiftsModel(
      state,
      action: PayloadAction<IAnalyzeChannelsChannelShareShiftsModel | null>
    ) {
      state.analyzeChannelsChannelShareShiftsModel = action.payload;
    },
    setAnalyzeChannelsChannelShareShiftsModelError(state, action: PayloadAction<IError | null>) {
      state.analyzeChannelsChannelShareShiftsModelError = action.payload;
    },
  },
});

export const {
  // stores
  setAnalyzeChannelsStoreCollectionLoading,
  setAnalyzeChannelsStoreCollection,
  setAnalyzeChannelsStoreCollectionError,
  // weeks
  setAnalyzeChannelsWeekCollectionLoading,
  setAnalyzeChannelsWeekCollection,
  setAnalyzeChannelsWeekCollectionError,
  // channel share
  setAnalyzeChannelsChannelShareModelLoading,
  setAnalyzeChannelsChannelShareModel,
  setAnalyzeChannelsChannelShareModelError,
  // channel share weekly
  setAnalyzeChannelsChannelShareWeeklyModelLoading,
  setAnalyzeChannelsChannelShareWeeklyModel,
  setAnalyzeChannelsChannelShareWeeklyModelError,
  // channel share shifts
  setAnalyzeChannelsChannelShareShiftsModelLoading,
  setAnalyzeChannelsChannelShareShiftsModel,
  setAnalyzeChannelsChannelShareShiftsModelError,
} = analyzeChannelsSlice.actions;
export default analyzeChannelsSlice.reducer;

// Thunks

export const getAnalyzeChannelsStoreCollection = () => {
  async function thunk(dispatch: AppDispatch): Promise<void> {
    dispatch(setAnalyzeChannelsStoreCollectionLoading(true));

    try {
      const data = await analyzeChannelsTransport.getStores();
      dispatch(setAnalyzeChannelsStoreCollection(data));
    } catch (error) {
      dispatch(setAnalyzeChannelsStoreCollectionError(error as IError));
    } finally {
      dispatch(setAnalyzeChannelsStoreCollectionLoading(false));
    }
  }

  return thunk;
};

export const getAnalyzeChannelsWeekCollection = () => {
  async function thunk(dispatch: AppDispatch): Promise<void> {
    dispatch(setAnalyzeChannelsWeekCollectionLoading(true));

    try {
      const data = await analyzeChannelsTransport.getWeeks();
      dispatch(setAnalyzeChannelsWeekCollection(data));
    } catch (error) {
      dispatch(setAnalyzeChannelsWeekCollectionError(error as IError));
    } finally {
      dispatch(setAnalyzeChannelsWeekCollectionLoading(false));
    }
  }

  return thunk;
};

export const getAnalyzeChannelsChannelShareModel = (params?: IAnalyzeChannelsChannelShareParams) => {
  async function thunk(dispatch: AppDispatch): Promise<void> {
    dispatch(setAnalyzeChannelsChannelShareModelLoading(true));

    try {
      const data = await analyzeChannelsTransport.getChannelShare(params);
      dispatch(setAnalyzeChannelsChannelShareModel(data));
    } catch (error) {
      dispatch(setAnalyzeChannelsChannelShareModelError(error as IError));
    } finally {
      dispatch(setAnalyzeChannelsChannelShareModelLoading(false));
    }
  }

  return thunk;
};

export const getAnalyzeChannelsChannelShareWeeklyModel = (params?: IAnalyzeChannelsChannelShareWeeklyParams) => {
  async function thunk(dispatch: AppDispatch): Promise<void> {
    dispatch(setAnalyzeChannelsChannelShareWeeklyModelLoading(true));

    try {
      const data = await analyzeChannelsTransport.getChannelShareWeekly(params);
      dispatch(setAnalyzeChannelsChannelShareWeeklyModel(data));
    } catch (error) {
      dispatch(setAnalyzeChannelsChannelShareWeeklyModelError(error as IError));
    } finally {
      dispatch(setAnalyzeChannelsChannelShareWeeklyModelLoading(false));
    }
  }

  return thunk;
};

export const getAnalyzeChannelsChannelShareShiftsModel = (params?: IAnalyzeChannelsChannelShareShiftsParams) => {
  async function thunk(dispatch: AppDispatch): Promise<void> {
    dispatch(setAnalyzeChannelsChannelShareShiftsModelLoading(true));

    try {
      const data = await analyzeChannelsTransport.getChannelShareShifts(params);
      dispatch(setAnalyzeChannelsChannelShareShiftsModel(data));
    } catch (error) {
      dispatch(setAnalyzeChannelsChannelShareShiftsModelError(error as IError));
    } finally {
      dispatch(setAnalyzeChannelsChannelShareShiftsModelLoading(false));
    }
  }

  return thunk;
};
